import { useState } from "react";
import {
  BrainCircuit,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Wrench,
  X,
} from "lucide-react";
import {
  useAcceptFinding,
  useDismissFinding,
  useMetaFindings,
  useMetaRuns,
} from "../api/aronMetaApi";
import type { MetaAronFinding, MetaAronRun } from "../types";
import { AronMarkdown } from "../components/AronMarkdown";
import { formatDateTime } from "@/utils/formatDate";

const KIND_STYLE: Record<string, string> = {
  knowledge: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  feature: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  bug: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  prompt_rule: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

const CONFIDENCE_STYLE: Record<string, string> = {
  high: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  low: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
};

const KIND_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "Wszystkie" },
  { value: "knowledge", label: "Wiedza" },
  { value: "feature", label: "Funkcje" },
  { value: "bug", label: "Bugi" },
  { value: "prompt_rule", label: "Reguły promptu" },
];

function LastRunCard({ run }: { run: MetaAronRun }) {
  const stat = (label: string, value: string | number) => (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value}</div>
    </div>
  );
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs text-gray-500">Ostatni przebieg: {formatDateTime(run.created_at)}</span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-700">
          {run.status_display} · {run.model_id}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
        {stat("Rozmowy", run.conversations_analyzed)}
        {stat("Sygnały", run.signals_count)}
        {stat("Ustalenia", run.findings_count)}
        {stat("Tokeny", `${run.input_tokens + run.output_tokens}`)}
        {stat("Koszt", `$${run.cost_usd}`)}
      </div>
      {run.status === "skipped" && run.error && (
        <p className="mt-2 text-xs italic text-gray-400">{run.error}</p>
      )}
    </div>
  );
}

function FindingCard({ finding }: { finding: MetaAronFinding }) {
  const [open, setOpen] = useState(false);
  const accept = useAcceptFinding();
  const dismiss = useDismissFinding();
  const busy = accept.isPending || dismiss.isPending;

  const isKnowledge = finding.kind === "knowledge";
  const notePending = finding.knowledge_note_status === "pending";
  const noteActive = finding.knowledge_note_status === "active";
  const dismissed = finding.status === "dismissed";
  const duplicate = finding.status === "duplicate";

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start gap-2">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-start gap-1.5 text-left"
        >
          {open ? (
            <ChevronDown size={16} className="mt-0.5 shrink-0 text-gray-400" />
          ) : (
            <ChevronRight size={16} className="mt-0.5 shrink-0 text-gray-400" />
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                  KIND_STYLE[finding.kind] ?? KIND_STYLE.feature
                }`}
              >
                {finding.kind_display}
              </span>
              {finding.confidence && (
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    CONFIDENCE_STYLE[finding.confidence] ?? CONFIDENCE_STYLE.low
                  }`}
                >
                  {finding.confidence}
                </span>
              )}
              <span className="font-medium text-gray-900 dark:text-gray-100">{finding.title}</span>
            </div>
            {finding.summary && <p className="mt-0.5 truncate text-xs text-gray-500">{finding.summary}</p>}
          </div>
        </button>
        <div className="shrink-0">
          {dismissed ? (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-700">
              Odrzucone
            </span>
          ) : duplicate ? (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-700">
              Duplikat
            </span>
          ) : noteActive ? (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              Zaakceptowane
            </span>
          ) : null}
        </div>
      </div>

      {open && (
        <div className="mt-2 space-y-2 border-t border-gray-100 pt-2 dark:border-gray-700">
          <AronMarkdown text={finding.detail} />
          {finding.why && (
            <p className="text-xs italic text-gray-400">Uzasadnienie: {finding.why}</p>
          )}
          {finding.evidence_conversation_ids.length > 0 && (
            <p className="text-xs text-gray-400">
              Dowód: {finding.evidence_conversation_ids.length} rozmów
            </p>
          )}
          {!isKnowledge && finding.dev_issue && (
            <p className="flex items-center gap-1 text-xs text-sky-600 dark:text-sky-400">
              <Wrench size={12} /> Zgłoszenie techniczne: {finding.dev_issue_title}
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {isKnowledge && notePending && (
              <button
                onClick={() => accept.mutate(finding.id)}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {accept.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                Akceptuj wiedzę
              </button>
            )}
            {!dismissed && !duplicate && !noteActive && (
              <button
                onClick={() => dismiss.mutate(finding.id)}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <X size={14} /> Odrzuć
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function AronMetaPage() {
  const [kind, setKind] = useState<string>("all");
  const runs = useMetaRuns();
  const findings = useMetaFindings(kind === "all" ? undefined : kind);

  const lastRun = runs.data?.[0];

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 via-red-700 to-black text-white">
          <BrainCircuit size={16} />
        </span>
        <div>
          <h1 className="text-lg font-semibold">META-ARON</h1>
          <p className="text-xs text-gray-500">
            Analityk nad ARON-em: czyta wszystkie rozmowy i sygnały, proponuje wiedzę, funkcje
            i poprawki. Wiedza czeka tu na akceptację; bugi/funkcje trafiają do zgłoszeń
            technicznych jako „ARON".
          </p>
        </div>
      </div>

      {runs.isLoading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" /> Wczytuję przebiegi…
        </div>
      ) : lastRun ? (
        <LastRunCard run={lastRun} />
      ) : (
        <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400 dark:border-gray-700">
          Brak przebiegów META-ARON. Analiza uruchamia się codziennie rano.
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {KIND_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setKind(f.value)}
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${
              kind === f.value
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {findings.isLoading ? (
        <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" /> Wczytuję ustalenia…
        </div>
      ) : findings.data && findings.data.length > 0 ? (
        <div className="space-y-3">
          {findings.data.map((f) => (
            <FindingCard key={f.id} finding={f} />
          ))}
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400 dark:border-gray-700">
          Brak ustaleń{kind !== "all" ? " w tej kategorii" : ""}.
        </p>
      )}
    </div>
  );
}
